import { create } from 'zustand';
import { User, Organization, Workplace, Partner, TimeZone } from 'types';
import {
	userSettingsService,
	CreateUserRequest,
	UpdateUserRequest,
	GetUsersParams,
} from 'core/API/userSettings';
import {
	organizationSettingsService,
} from 'core/API/organizationSettings';
import {
	GetOrganizationsParams,
	CreateOrganizationRequest,
	UpdateOrganizationRequest,
	ApiDayPart,
} from 'types/Organization';
import { GetTimeZones } from 'core/API/workplaces';
import { fetchWorkplaces, deleteWorkplace as apiDeleteWorkplace } from 'core/API/workPlace';
import { getPartners, deletePartner as apiDeletePartner } from 'core/API/partners';
import {
	getSecurityRoles,
	SecurityRoleResponse,
} from 'core/API/securityRoles';
import {
	getCompleteHierarchy,
	getHierarchyNodes,
	CompleteHierarchyResponse,
	HierarchyNode,
	GetHierarchyParams,
} from 'core/API/hierarchy';
import { isHiddenSecurityRole } from 'module/settings/settingsGameMasterTabs/GeneralSetting/constants';
import useUserDataStore, { STORAGE_KEYS } from './useUserDataStore';

type FetchOptions = { force?: boolean };

interface ListMeta {
	total: number;
	page: number;
	limit: number;
}

interface SettingsState {
	// ── Users ──────────────────────────────────────────────
	users: User[];
	usersLoading: boolean;
	usersError: string | null;
	usersLoaded: boolean;
	usersWorkplaceId: number | null;
	usersMeta: ListMeta;
	selectedUser: User | null;

	// ── Organizations ──────────────────────────────────────
	organizations: Organization[];
	organizationsLoading: boolean;
	organizationsError: string | null;
	organizationsLoaded: boolean;
	organizationsMeta: ListMeta;
	selectedOrganization: Organization | null;
	organisationTypes: { id: number; name: string }[];
	brands: { label: string }[];
	timeZones: TimeZone[];
	days: {
		'@id': string;
		'@type': string;
		id: number;
		label: string;
		locale: string;
		position: string;
	}[];
	dayParts: ApiDayPart[];
	lookupsLoaded: boolean;

	// ── Workplaces ─────────────────────────────────────────
	workplaces: Workplace[];
	workplacesLoading: boolean;
	workplacesError: string | null;
	workplacesLoaded: boolean;

	// ── Partners ───────────────────────────────────────────
	partners: Partner[];
	partnersLoading: boolean;
	partnersError: string | null;
	partnersLoaded: boolean;

	// ── Security Roles (raw API responses) ─────────────────
	securityRoles: SecurityRoleResponse[];
	securityRolesLoading: boolean;
	securityRolesError: string | null;
	securityRolesLoaded: boolean;

	// ── Hierarchy ──────────────────────────────────────────
	completeHierarchy: CompleteHierarchyResponse | null;
	completeHierarchyLoading: boolean;
	completeHierarchyError: string | null;
	completeHierarchyLoaded: boolean;
	hierarchyNodes: HierarchyNode[];
	hierarchyNodesLoading: boolean;
	hierarchyNodesError: string | null;
	hierarchyNodesLoaded: boolean;

	// ── User actions ───────────────────────────────────────
	fetchUsers: (params?: GetUsersParams, options?: FetchOptions) => Promise<void>;
	createUser: (userData: CreateUserRequest) => Promise<User | null>;
	updateUser: (userData: UpdateUserRequest) => Promise<User | null>;
	deleteUser: (userId: number) => Promise<void>;
	toggleUserStatus: (userId: number, active: boolean) => Promise<void>;
	addUser: (user: User) => void;
	patchUser: (user: User) => void;
	removeUser: (userId: number) => void;
	setSelectedUser: (user: User | null) => void;

	// ── Organization actions ───────────────────────────────
	fetchOrganizations: (params?: GetOrganizationsParams, options?: FetchOptions) => Promise<void>;
	createOrganization: (orgData: CreateOrganizationRequest) => Promise<Organization | null>;
	updateOrganization: (orgData: UpdateOrganizationRequest) => Promise<Organization | null>;
	deleteOrganization: (organizationId: number) => Promise<void>;
	addOrganization: (org: Organization) => void;
	patchOrganization: (org: Organization) => void;
	removeOrganization: (organizationId: number) => void;
	setSelectedOrganization: (organization: Organization | null) => void;
	fetchOrganisationTypes: () => Promise<void>;
	fetchBrands: () => Promise<void>;
	fetchTimeZones: () => Promise<void>;
	fetchDays: () => Promise<void>;
	fetchDayParts: (organizationId?: number, options?: FetchOptions) => Promise<void>;
	createDayPart: (
		organizationId: number,
		dayPartData: { partName: string; startTime: string; endTime: string }
	) => Promise<ApiDayPart | null>;
	updateDayPart: (
		dayPartId: number,
		dayPartData: { organization: string; partName: string; startTime: string; endTime: string }
	) => Promise<ApiDayPart | null>;
	deleteDayPart: (dayPartId: number) => Promise<void>;

	// ── Workplace actions ──────────────────────────────────
	fetchWorkplacesList: (options?: FetchOptions) => Promise<void>;
	addWorkplace: (workplace: Workplace) => void;
	patchWorkplace: (workplace: Workplace) => void;
	removeWorkplace: (workplaceId: number) => Promise<void>;

	// ── Partner actions ────────────────────────────────────
	fetchPartners: (options?: FetchOptions) => Promise<void>;
	addPartner: (partner: Partner) => void;
	patchPartner: (partner: Partner) => void;
	removePartner: (partner: Partner) => Promise<void>;

	// ── Security role actions ──────────────────────────────
	fetchSecurityRoles: (options?: FetchOptions) => Promise<void>;
	setSecurityRoles: (roles: SecurityRoleResponse[]) => void;
	addSecurityRole: (role: SecurityRoleResponse) => void;
	patchSecurityRole: (role: SecurityRoleResponse) => void;
	removeSecurityRole: (roleId: number) => void;

	// ── Hierarchy actions ──────────────────────────────────
	fetchCompleteHierarchy: (params?: GetHierarchyParams, options?: FetchOptions) => Promise<void>;
	setCompleteHierarchy: (data: CompleteHierarchyResponse | null) => void;
	fetchHierarchyNodes: (options?: FetchOptions) => Promise<void>;
	setHierarchyNodes: (nodes: HierarchyNode[]) => void;

	// ── Global ─────────────────────────────────────────────
	clearSettingsCache: () => void;
}

const defaultMeta: ListMeta = { total: 0, page: 1, limit: 50 };

const sortWorkplaces = (workplaces: Workplace[]): Workplace[] =>
	[...workplaces].sort((a, b) => {
		const unitA = parseInt(a.workplaceUnitNumber || '0', 10);
		const unitB = parseInt(b.workplaceUnitNumber || '0', 10);
		return unitA - unitB;
	});

const syncSecurityRolesToUserDataStore = (roles: SecurityRoleResponse[]) => {
	const filtered = roles.filter((role) => !isHiddenSecurityRole(role.name));
	useUserDataStore.getState().setSecurityRoles(filtered);
};

const syncHierarchyNodesToUserDataStore = (nodes: HierarchyNode[]) => {
	useUserDataStore.getState().setHierarchyNodes(nodes);
};

const useSettingsStore = create<SettingsState>((set, get) => ({
	// ── Initial state ──────────────────────────────────────
	users: [],
	usersLoading: false,
	usersError: null,
	usersLoaded: false,
	usersWorkplaceId: null,
	usersMeta: { ...defaultMeta },
	selectedUser: null,

	organizations: [],
	organizationsLoading: false,
	organizationsError: null,
	organizationsLoaded: false,
	organizationsMeta: { ...defaultMeta },
	selectedOrganization: null,
	organisationTypes: [],
	brands: [],
	timeZones: [],
	days: [],
	dayParts: [],
	lookupsLoaded: false,

	workplaces: [],
	workplacesLoading: false,
	workplacesError: null,
	workplacesLoaded: false,

	partners: [],
	partnersLoading: false,
	partnersError: null,
	partnersLoaded: false,

	securityRoles: [],
	securityRolesLoading: false,
	securityRolesError: null,
	securityRolesLoaded: false,

	completeHierarchy: null,
	completeHierarchyLoading: false,
	completeHierarchyError: null,
	completeHierarchyLoaded: false,
	hierarchyNodes: [],
	hierarchyNodesLoading: false,
	hierarchyNodesError: null,
	hierarchyNodesLoaded: false,

	// ── Users ──────────────────────────────────────────────
	fetchUsers: async (params, options) => {
		const workplaceId = params?.workplaceId ?? null;
		const { usersLoaded, usersWorkplaceId, usersLoading } = get();

		if (usersLoading) return;
		if (usersLoaded && usersWorkplaceId === workplaceId && !options?.force) return;

		set({ usersLoading: true, usersError: null });
		try {
			const response = await userSettingsService.getUsers(params);
			set({
				users: response.data,
				usersMeta: response.meta,
				usersWorkplaceId: workplaceId,
				usersLoaded: true,
				usersLoading: false,
				usersError: null,
			});
		} catch (err: any) {
			set({
				usersLoading: false,
				usersError: err?.message || 'Failed to fetch users',
			});
		}
	},

	createUser: async (userData) => {
		try {
			const newUser = await userSettingsService.createUser(userData);
			get().addUser(newUser);
			return newUser;
		} catch (err: any) {
			set({ usersError: err?.message || 'Failed to create user' });
			return null;
		}
	},

	updateUser: async (userData) => {
		try {
			const updatedUser = await userSettingsService.updateUser(userData);
			get().patchUser(updatedUser);
			return updatedUser;
		} catch (err: any) {
			set({ usersError: err?.message || 'Failed to update user' });
			return null;
		}
	},

	deleteUser: async (userId) => {
		try {
			await userSettingsService.deleteUser(userId);
			get().removeUser(userId);
		} catch (err: any) {
			set({ usersError: err?.message || 'Failed to delete user' });
			throw err;
		}
	},

	toggleUserStatus: async (userId, active) => {
		try {
			const updatedUser = await userSettingsService.toggleUserStatus(userId, active);
			get().patchUser(updatedUser);
		} catch (err: any) {
			set({ usersError: err?.message || 'Failed to toggle user status' });
			throw err;
		}
	},

	addUser: (user) => {
		set((state) => ({
			users: [...state.users, user],
			usersMeta: { ...state.usersMeta, total: state.usersMeta.total + 1 },
		}));
	},

	patchUser: (user) => {
		const targetId = Number(user.id);
		set((state) => ({
			users: state.users.map((u) => {
				if (Number(u.id) !== targetId) return u;

				const incomingHierarchy =
					user.hierarchy && typeof user.hierarchy === 'object' ? user.hierarchy : null;
				const hasUsableHierarchy =
					incomingHierarchy &&
					(Boolean(incomingHierarchy.name) ||
						Boolean(incomingHierarchy['@id']) ||
						incomingHierarchy.id != null);

				const nextHierarchy = hasUsableHierarchy
					? {
							...(typeof u.hierarchy === 'object' && u.hierarchy ? u.hierarchy : {}),
							...incomingHierarchy,
						}
					: u.hierarchy;

				const incomingRole =
					user.securityRole && typeof user.securityRole === 'object'
						? user.securityRole
						: null;
				const hasUsableRole =
					incomingRole && (Boolean(incomingRole.name) || Boolean(incomingRole['@id']));

				const nextSecurityRole = hasUsableRole
					? {
							...(typeof u.securityRole === 'object' && u.securityRole
								? u.securityRole
								: {}),
							...incomingRole,
						}
					: u.securityRole;

				return {
					...u,
					...user,
					id: targetId,
					hierarchy: nextHierarchy as typeof u.hierarchy,
					securityRole: nextSecurityRole as typeof u.securityRole,
				};
			}),
			selectedUser:
				state.selectedUser && Number(state.selectedUser.id) === targetId
					? ({
							...state.selectedUser,
							...user,
							id: targetId,
						} as typeof state.selectedUser)
					: state.selectedUser,
		}));
	},

	removeUser: (userId) => {
		set((state) => ({
			users: state.users.filter((u) => u.id !== userId),
			selectedUser: state.selectedUser?.id === userId ? null : state.selectedUser,
			usersMeta: {
				...state.usersMeta,
				total: Math.max(0, state.usersMeta.total - 1),
			},
		}));
	},

	setSelectedUser: (user) => set({ selectedUser: user }),

	// ── Organizations ──────────────────────────────────────
	fetchOrganizations: async (params, options) => {
		const { organizationsLoaded, organizationsLoading } = get();
		if (organizationsLoading) return;
		if (organizationsLoaded && !options?.force) return;

		set({ organizationsLoading: true, organizationsError: null });
		try {
			const response = await organizationSettingsService.getOrganizations(params);
			set({
				organizations: response.data?.[0] ? [response.data[0]] : [],
				organizationsMeta: response.meta,
				organizationsLoaded: true,
				organizationsLoading: false,
				organizationsError: null,
			});
		} catch (err: any) {
			set({
				organizationsLoading: false,
				organizationsError: err?.message || 'Failed to fetch organizations',
			});
		}
	},

	createOrganization: async (orgData) => {
		try {
			const newOrganization = await organizationSettingsService.createOrganization(orgData);
			get().addOrganization(newOrganization);
			return newOrganization;
		} catch (err: any) {
			set({ organizationsError: err?.message || 'Failed to create organization' });
			return null;
		}
	},

	updateOrganization: async (orgData) => {
		try {
			const updatedOrganization = await organizationSettingsService.updateOrganization(orgData);
			get().patchOrganization(updatedOrganization);
			return updatedOrganization;
		} catch (err: any) {
			set({ organizationsError: err?.message || 'Failed to update organization' });
			return null;
		}
	},

	deleteOrganization: async (organizationId) => {
		try {
			await organizationSettingsService.deleteOrganization(organizationId);
			get().removeOrganization(organizationId);
		} catch (err: any) {
			set({ organizationsError: err?.message || 'Failed to delete organization' });
			throw err;
		}
	},

	addOrganization: (org) => {
		set((state) => ({
			organizations: [...state.organizations, org],
			organizationsMeta: {
				...state.organizationsMeta,
				total: state.organizationsMeta.total + 1,
			},
		}));
	},

	patchOrganization: (org) => {
		set((state) => ({
			organizations: state.organizations.map((o) => (o.id === org.id ? org : o)),
			selectedOrganization:
				state.selectedOrganization?.id === org.id ? org : state.selectedOrganization,
		}));
	},

	removeOrganization: (organizationId) => {
		set((state) => ({
			organizations: state.organizations.filter((o) => o.id !== organizationId),
			selectedOrganization:
				state.selectedOrganization?.id === organizationId ? null : state.selectedOrganization,
			organizationsMeta: {
				...state.organizationsMeta,
				total: Math.max(0, state.organizationsMeta.total - 1),
			},
		}));
	},

	setSelectedOrganization: (organization) => set({ selectedOrganization: organization }),

	fetchOrganisationTypes: async () => {
		try {
			const types = await organizationSettingsService.getOrganisationTypes();
			set({ organisationTypes: types });
		} catch (err) {
			console.error('Failed to fetch organisation types:', err);
		}
	},

	fetchBrands: async () => {
		try {
			const brands = await organizationSettingsService.getBrands();
			set({ brands });
		} catch (err) {
			console.error('Failed to fetch brands:', err);
		}
	},

	fetchTimeZones: async () => {
		try {
			const zones = await GetTimeZones();
			set({ timeZones: zones });
		} catch (err) {
			console.error('Failed to fetch time zones:', err);
		}
	},

	fetchDays: async () => {
		try {
			const days = await organizationSettingsService.getDays();
			set({ days });
		} catch (err) {
			console.error('Failed to fetch days:', err);
		}
	},

	fetchDayParts: async (organizationId, options) => {
		const { dayParts, lookupsLoaded } = get();
		if (lookupsLoaded && dayParts.length > 0 && !options?.force) return;
		try {
			const parts = await organizationSettingsService.getDayParts(organizationId);
			set({ dayParts: parts, lookupsLoaded: true });
		} catch (err) {
			console.error('Failed to fetch day parts:', err);
		}
	},

	createDayPart: async (organizationId, dayPartData) => {
		try {
			const newDayPart = await organizationSettingsService.createDayPart({
				organization: `/api/organizations/${organizationId}`,
				...dayPartData,
			});
			set((state) => ({ dayParts: [...state.dayParts, newDayPart] }));
			return newDayPart;
		} catch (err: any) {
			set({ organizationsError: err?.message || 'Failed to create day part' });
			return null;
		}
	},

	updateDayPart: async (dayPartId, dayPartData) => {
		try {
			const updatedDayPart = await organizationSettingsService.updateDayPart(
				dayPartId,
				dayPartData
			);
			set((state) => ({
				dayParts: state.dayParts.map((dp) => (dp.id === dayPartId ? updatedDayPart : dp)),
			}));
			return updatedDayPart;
		} catch (err: any) {
			set({ organizationsError: err?.message || 'Failed to update day part' });
			return null;
		}
	},

	deleteDayPart: async (dayPartId) => {
		try {
			await organizationSettingsService.deleteDayPart(dayPartId);
			set((state) => ({
				dayParts: state.dayParts.filter((dp) => dp.id !== dayPartId),
			}));
		} catch (err: any) {
			set({ organizationsError: err?.message || 'Failed to delete day part' });
			throw err;
		}
	},

	// ── Workplaces ─────────────────────────────────────────
	fetchWorkplacesList: async (options) => {
		const { workplacesLoaded, workplacesLoading } = get();
		if (workplacesLoading) return;
		if (workplacesLoaded && !options?.force) return;

		set({ workplacesLoading: true, workplacesError: null });
		try {
			const response = await fetchWorkplaces({
				limit: 30,
				page: 1,
				create_predictions: 0,
			});
			set({
				workplaces: sortWorkplaces(response || []),
				workplacesLoaded: true,
				workplacesLoading: false,
				workplacesError: null,
			});
		} catch (err: any) {
			set({
				workplacesLoading: false,
				workplacesError: err?.message || 'Failed to fetch workplaces',
			});
		}
	},

	addWorkplace: (workplace) => {
		set((state) => ({
			workplaces: sortWorkplaces([...state.workplaces, workplace]),
		}));
	},

	patchWorkplace: (workplace) => {
		set((state) => ({
			workplaces: sortWorkplaces(
				state.workplaces.map((wp) => (wp.id === workplace.id ? { ...wp, ...workplace } : wp))
			),
		}));
	},

	removeWorkplace: async (workplaceId) => {
		await apiDeleteWorkplace(workplaceId);
		set((state) => ({
			workplaces: state.workplaces.filter((wp) => wp.id !== workplaceId),
		}));
	},

	// ── Partners ───────────────────────────────────────────
	fetchPartners: async (options) => {
		const { partnersLoaded, partnersLoading } = get();
		if (partnersLoading) return;
		if (partnersLoaded && !options?.force) return;

		set({ partnersLoading: true, partnersError: null });
		try {
			const partnersList = await getPartners({ limit: 30, page: 1 });
			set({
				partners: partnersList,
				partnersLoaded: true,
				partnersLoading: false,
				partnersError: null,
			});
		} catch (err: any) {
			set({
				partnersLoading: false,
				partnersError: err?.message || 'Failed to fetch partners',
				partners: [],
			});
		}
	},

	addPartner: (partner) => {
		set((state) => ({ partners: [...state.partners, partner] }));
	},

	patchPartner: (partner) => {
		set((state) => ({
			partners: state.partners.map((p) => (p.id === partner.id ? partner : p)),
		}));
	},

	removePartner: async (partner) => {
		await apiDeletePartner(partner);
		const partnerId =
			typeof partner === 'object' && partner !== null
				? (partner as Partner).id
				: partner;
		set((state) => ({
			partners: state.partners.filter((p) => p.id !== partnerId),
		}));
	},

	// ── Security Roles ─────────────────────────────────────
	fetchSecurityRoles: async (options) => {
		const { securityRolesLoaded, securityRolesLoading } = get();
		if (securityRolesLoading) return;
		if (securityRolesLoaded && !options?.force) return;

		set({ securityRolesLoading: true, securityRolesError: null });
		try {
			const response = await getSecurityRoles({ page: 1, itemsPerPage: 100 });
			const filteredRoles = response.filter((role) => !isHiddenSecurityRole(role.name));
			set({
				securityRoles: filteredRoles,
				securityRolesLoaded: true,
				securityRolesLoading: false,
				securityRolesError: null,
			});
			syncSecurityRolesToUserDataStore(filteredRoles);
			localStorage.setItem(STORAGE_KEYS.SECURITY_ROLES, JSON.stringify(filteredRoles));
		} catch (err: any) {
			set({
				securityRolesLoading: false,
				securityRolesError: err?.message || 'Failed to fetch security roles',
			});
		}
	},

	setSecurityRoles: (roles) => {
		const filteredRoles = roles.filter((role) => !isHiddenSecurityRole(role.name));
		set({ securityRoles: filteredRoles, securityRolesLoaded: true });
		syncSecurityRolesToUserDataStore(filteredRoles);
		localStorage.setItem(STORAGE_KEYS.SECURITY_ROLES, JSON.stringify(filteredRoles));
	},

	addSecurityRole: (role) => {
		set((state) => {
			const next = [...state.securityRoles, role].filter(
				(r) => !isHiddenSecurityRole(r.name)
			);
			syncSecurityRolesToUserDataStore(next);
			localStorage.setItem(STORAGE_KEYS.SECURITY_ROLES, JSON.stringify(next));
			return { securityRoles: next, securityRolesLoaded: true };
		});
	},

	patchSecurityRole: (role) => {
		set((state) => {
			const next = state.securityRoles.map((r) => (r.id === role.id ? role : r));
			syncSecurityRolesToUserDataStore(next);
			localStorage.setItem(STORAGE_KEYS.SECURITY_ROLES, JSON.stringify(next));
			return { securityRoles: next };
		});
	},

	removeSecurityRole: (roleId) => {
		set((state) => {
			const next = state.securityRoles.filter((r) => r.id !== roleId);
			syncSecurityRolesToUserDataStore(next);
			localStorage.setItem(STORAGE_KEYS.SECURITY_ROLES, JSON.stringify(next));
			return { securityRoles: next };
		});
	},

	// ── Hierarchy ──────────────────────────────────────────
	fetchCompleteHierarchy: async (params, options) => {
		const { completeHierarchyLoaded, completeHierarchyLoading } = get();
		if (completeHierarchyLoading) return;
		if (completeHierarchyLoaded && !options?.force) return;

		set({ completeHierarchyLoading: true, completeHierarchyError: null });
		try {
			const response = await getCompleteHierarchy({
				page: params?.page || 1,
				itemsPerPage: params?.itemsPerPage || 50,
				inFocus: params?.inFocus || null,
			});
			set({
				completeHierarchy: response,
				completeHierarchyLoaded: true,
				completeHierarchyLoading: false,
				completeHierarchyError: null,
			});
		} catch (err: any) {
			set({
				completeHierarchyLoading: false,
				completeHierarchyError: err?.message || 'Failed to fetch hierarchy',
			});
		}
	},

	setCompleteHierarchy: (data) => {
		set({
			completeHierarchy: data,
			completeHierarchyLoaded: data !== null,
		});
	},

	fetchHierarchyNodes: async (options) => {
		const { hierarchyNodesLoaded, hierarchyNodesLoading } = get();
		if (hierarchyNodesLoading) return;
		if (hierarchyNodesLoaded && !options?.force) return;

		set({ hierarchyNodesLoading: true, hierarchyNodesError: null });
		try {
			const nodes = await getHierarchyNodes({ page: 1, itemsPerPage: 100 });
			set({
				hierarchyNodes: nodes,
				hierarchyNodesLoaded: true,
				hierarchyNodesLoading: false,
				hierarchyNodesError: null,
			});
			syncHierarchyNodesToUserDataStore(nodes);
			localStorage.setItem(STORAGE_KEYS.HIERARCHY_NODES, JSON.stringify(nodes));
		} catch (err: any) {
			set({
				hierarchyNodesLoading: false,
				hierarchyNodesError: err?.message || 'Failed to fetch hierarchy nodes',
			});
		}
	},

	setHierarchyNodes: (nodes) => {
		set({ hierarchyNodes: nodes, hierarchyNodesLoaded: true });
		syncHierarchyNodesToUserDataStore(nodes);
		localStorage.setItem(STORAGE_KEYS.HIERARCHY_NODES, JSON.stringify(nodes));
	},

	clearSettingsCache: () => {
		set({
			users: [],
			usersLoading: false,
			usersError: null,
			usersLoaded: false,
			usersWorkplaceId: null,
			usersMeta: { ...defaultMeta },
			selectedUser: null,
			organizations: [],
			organizationsLoading: false,
			organizationsError: null,
			organizationsLoaded: false,
			organizationsMeta: { ...defaultMeta },
			selectedOrganization: null,
			organisationTypes: [],
			brands: [],
			timeZones: [],
			days: [],
			dayParts: [],
			lookupsLoaded: false,
			workplaces: [],
			workplacesLoading: false,
			workplacesError: null,
			workplacesLoaded: false,
			partners: [],
			partnersLoading: false,
			partnersError: null,
			partnersLoaded: false,
			securityRoles: [],
			securityRolesLoading: false,
			securityRolesError: null,
			securityRolesLoaded: false,
			completeHierarchy: null,
			completeHierarchyLoading: false,
			completeHierarchyError: null,
			completeHierarchyLoaded: false,
			hierarchyNodes: [],
			hierarchyNodesLoading: false,
			hierarchyNodesError: null,
			hierarchyNodesLoaded: false,
		});
	},
}));

export default useSettingsStore;
